import { Alert, Button, MenuItem, Stack, TextField } from '@mui/material';
import { useSkinStyles } from '../../../hooks/useSkinStyles';
import useLocalization from '../../../hooks/useLocalization';
import { useZeroXDeposit } from '../../../hooks/payment/useZeroXDeposit';
import { zeroXDepositStyles as styles } from '../../../styles/components/zeroXDeposit';
import { zeroXCashierFieldStyles } from '../../../styles/components/zeroXCashier';
import ZeroXActionButton from './ZeroXActionButton';
import ZeroXDepositDestination from './ZeroXDepositDestination';
import ZeroXDepositSkeleton from './ZeroXDepositSkeleton';
import ZeroXDepositMinimum, { getZeroXDepositMinimumHelp } from './ZeroXDepositMinimum';
import { mergeSx } from '../../../utils/styles';

type DepositContentProps = Pick<ReturnType<typeof useZeroXDeposit>, 'phase' | 'retry' | 'currency'>;

const DepositContent = ({ phase, retry, currency }: DepositContentProps) => {
  const { t } = useLocalization();

  switch (phase.name) {
    case 'selecting':
      return null;

    case 'loading':
      return <ZeroXDepositSkeleton currency={currency} />;

    case 'error':
      return (
        <>
          <Alert severity='error'>{t(phase.messageKey)}</Alert>
          {phase.canRetry && <ZeroXActionButton onClick={retry} label={t('retry')} />}
        </>
      );

    case 'pending':
      return (
        <>
          <Alert severity='info'>{t('zeroXPending')}</Alert>
          <ZeroXActionButton onClick={retry} label={t('zeroXCheckAddressStatus')} />
        </>
      );

    case 'ready': {
      const { destination } = phase;
      const destinationKey = JSON.stringify([destination.currency, destination.address, destination.tag]);

      return <ZeroXDepositDestination key={destinationKey} destination={destination} />;
    }
  }
};

const ZeroXDepositForm = () => {
  const { t } = useLocalization();
  const { select: currencyFieldSx, formInset } = useSkinStyles(zeroXCashierFieldStyles);
  const { currencies, currency, phase, selectCurrency, retry, minimum, retryMinimum } = useZeroXDeposit();
  const options = currencies.data?.currencies ?? [];
  const minimumHelp = currency ? getZeroXDepositMinimumHelp(t, currency, minimum) : undefined;

  if (currencies.isError) {
    return (
      <Alert
        severity='error'
        action={
          <Button variant='text' onClick={() => void currencies.refetch()}>
            {t('retry')}
          </Button>
        }
      >
        {t('zeroXUnavailable')}
      </Alert>
    );
  }

  if (!currencies.isLoading && !options.length) {
    return <Alert severity='info'>{t('zeroXUnavailable')}</Alert>;
  }

  return (
    <Stack sx={mergeSx(styles.root, formInset)}>
      <TextField
        select
        label={t('zeroXCurrency')}
        value={currency}
        aria-busy={currencies.isLoading}
        disabled={currencies.isLoading || phase.name === 'loading'}
        fullWidth
        sx={currencyFieldSx}
        helperText={minimumHelp}
        // The minimum arrives after selection, so announce it when it changes.
        slotProps={{ formHelperText: { role: 'status' } }}
        onChange={(event) => selectCurrency(event.target.value)}
      >
        {options.map((value) => (
          <MenuItem key={value} value={value}>
            {value}
          </MenuItem>
        ))}
      </TextField>

      {currency && <ZeroXDepositMinimum currency={currency} minimum={minimum} onRetry={retryMinimum} />}
      <DepositContent phase={phase} retry={retry} currency={currency} />
    </Stack>
  );
};

export default ZeroXDepositForm;
